import React, { useCallback, useMemo } from 'react';

import { Button } from '@material-ui/core';
import { TableCellProps } from 'components/table/table-types';
import { GeneralSearchEntityType, UrlRoute } from 'enums';
import { GeneralSearchValue } from 'types';
import {
  SearchResultDto,
  NewCentralFormType,
  SearchEntityType,
  SearchEntityTypeOptionId,
} from './types';
import { useSearchContext } from './context/search-context';
import { createRowCategoryOptionsV3 } from './v2/specificFilterRows/specific-filter-row-options-v3';
import {
  createOriginatorGeneralFilterRow,
  createOriginatorRelationFilterRow,
} from './search-result-filter-utils';

function getUrlRoute(type: GeneralSearchEntityType) {
  switch (type) {
    case GeneralSearchEntityType.ARCHIVAL_AID:
      return UrlRoute.ARCHIVAL_AID;
    case GeneralSearchEntityType.ARCHIVAL_COLLECTION:
      return UrlRoute.ARCHIVAL_COLLECTION;
    case GeneralSearchEntityType.ARCHIVAL_UNIT:
      return UrlRoute.ARCHIVAL_UNIT;
    case GeneralSearchEntityType.EAD_NODE:
      return UrlRoute.ARCHIVAL_AID;
    default:
      return;
  }
}

export function getGeneralSearchLink({
  history,
  column,
  row,
}: TableCellProps<GeneralSearchValue>) {
  const { id: columnId } = column;
  const { entityId, type } = row;

  const attribute = columnId as keyof GeneralSearchValue;
  const urlRoute = getUrlRoute(type);
  const url = `${urlRoute}/${row.level}/${entityId}${
    type === GeneralSearchEntityType.EAD_NODE ? '/ead' : ''
  }`;

  return (
    <Button
      component="span"
      variant="text"
      style={{ fontWeight: 'bold', textTransform: 'none' }}
      onClick={() => {
        history.push(url);
      }}
    >
      {row?.[attribute]}
    </Button>
  );
}

export const generalSearchOptions = [
  { id: GeneralSearchEntityType.ARCHIVAL_AID, label: 'Archivní pomůcka' },
  { id: GeneralSearchEntityType.ARCHIVAL_COLLECTION, label: 'Archivní fond' },
  { id: GeneralSearchEntityType.ARCHIVAL_UNIT, label: 'Archiválie' },
  { id: GeneralSearchEntityType.EAD_NODE, label: 'Uzel EAD' },
  { id: GeneralSearchEntityType.ORIGINATOR, label: 'Rejstříkové heslo' },
  { id: GeneralSearchEntityType.SERIES, label: 'Série' },
  { id: GeneralSearchEntityType.FILE, label: 'Složka' },
];

export const mapSearchEntityType: Record<
  SearchEntityType,
  SearchEntityTypeOptionId
> = {
  ARCHIVAL_AID: 'AID',
  ARCHIVAL_COLLECTION: 'COLLECTIONS',
  ARCHIVAL_UNIT: 'ARCHIVAL_UNIT_OR_EAD',
  EAD_NODE: 'ARCHIVAL_UNIT_OR_EAD',
  ORIGINATOR: 'ORIGINATOR',
};
// TODO: refactor
export const mapCategoryIdToSearchEntityType = (
  categoryId: string
): SearchEntityTypeOptionId | undefined => {
  switch (categoryId) {
    case 'AID':
      return 'AID';
    case 'COLLECTIONS':
      return 'COLLECTIONS';
    case 'ARCHIVAL_UNITS':
      return 'ARCHIVAL_UNIT_OR_EAD';
    case 'ORIGINATOR':
      return 'ORIGINATOR';
    case 'REGISTERS':
      return 'ORIGINATOR';
    default:
      return undefined;
  }
};

export const formatArchivalEntityTypeAggregations = (
  aggregations: { value: string; key: string }[]
): Record<SearchEntityTypeOptionId, number> => {
  const formatted: Record<string, number> = {};

  (aggregations as { value: string; key: SearchEntityType }[])
    .filter(({ value }) => !isNaN(parseInt(value)) && parseInt(value) !== 0)
    .forEach((agg) => {
      return (formatted[mapSearchEntityType[agg.key]] =
        (formatted[mapSearchEntityType[agg.key]] ?? 0) + parseInt(agg.value));
    });

  return formatted;
};

export const useUpdateSearchFormByResult = () => {
  const { currentFormValues, resetForm } = useSearchContext();
  const searchFilterRowOptions = useMemo(
    () => createRowCategoryOptionsV3(),
    []
  );

  const updateForm = useCallback(
    (item: SearchResultDto, originatorVariant?: 'RELATION' | 'ORIGINATOR') => {
      if (!currentFormValues) {
        console.log('DEBUG ERROR: no searchContext.currentFormValues');
        return;
      }
      switch (item.archivalEntityType) {
        case 'ARCHIVAL_COLLECTION':
        case 'ARCHIVAL_AID': {
          const mapEntityTypeToKey: Record<
            | GeneralSearchEntityType.ARCHIVAL_COLLECTION
            | GeneralSearchEntityType.ARCHIVAL_AID,
            keyof NewCentralFormType
          > = {
            ARCHIVAL_COLLECTION: 'selectedCollections',
            ARCHIVAL_AID: 'selectedAids',
          };

          const key = mapEntityTypeToKey[item.archivalEntityType] as
            | 'selectedCollections'
            | 'selectedAids';

          const oldItems = currentFormValues[key].items ?? [];

          if (oldItems.some((oi) => oi.id === item.id)) {
            return;
          }
          const add = currentFormValues[key].operation === 'include';
          const itemToAdd = { id: item.id, label: item?.label ?? '' };
          resetForm?.current?.({
            values: {
              ...currentFormValues,
              [key]: {
                operation: 'include',
                items: add ? [...oldItems, itemToAdd] : [itemToAdd],
              },
            },
          });
          break;
        }

        case 'ORIGINATOR': {
          if (originatorVariant === 'RELATION') {
            const filterOption = searchFilterRowOptions.find(
              (op) => op.id === 'archivalUnits-roles'
            );

            if (!filterOption) {
              console.error(
                'Unable to add relation filter: option archivalUnits-roles not found.'
              );
              return;
            }

            resetForm?.current?.({
              values: {
                ...currentFormValues,
                specificFilterRows: [
                  ...currentFormValues.specificFilterRows,
                  createOriginatorRelationFilterRow(item, filterOption),
                ],
              },
            });
          } else {
            const filterOption = searchFilterRowOptions.find((op) => {
              return op.id === 'originators-general';
            })!;
            resetForm?.current?.({
              values: {
                ...currentFormValues,
                specificFilterRows: [
                  ...currentFormValues.specificFilterRows,
                  createOriginatorGeneralFilterRow(item, filterOption),
                ],
              },
            });
          }
          break;
        }
      }
    },
    [currentFormValues, resetForm, searchFilterRowOptions]
  );

  return { updateForm, currentFormValues };
};
